import React, { useState } from 'react';
import {
  Route,
  CheckCircle2,
  Circle,
  Clock,
  Stethoscope,
  FlaskConical,
  Pill,
  Scan,
  CalendarCheck,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  MapPin,
  User,
  StickyNote,
  AlertCircle,
} from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { cn } from '@/lib/utils';
import { useCarePathways } from '../hooks/use-care-pathways';
import type { CarePathway, CarePathwayStep } from '../types/care-pathway-types';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import PageContainer from '@/components/layout/PageContainer';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import { Link } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function safeFormat(dateStr: string | undefined, fmt: string): string {
  if (!dateStr) return '';
  try {
    const d = parseISO(dateStr);
    return isValid(d) ? format(d, fmt) : dateStr;
  } catch {
    return dateStr;
  }
}

function getProgressPercent(pathway: CarePathway): number {
  const total = pathway.steps.length;
  if (total === 0) return 0;
  const done = pathway.steps.filter((s) => s.status === 'completed').length;
  return Math.round((done / total) * 100);
}

// ---------------------------------------------------------------------------
// Step type icon
// ---------------------------------------------------------------------------

const stepTypeIcon = (type: CarePathwayStep['type'], className = 'h-4 w-4') => {
  switch (type) {
    case 'consultation':
      return <Stethoscope className={className} />;
    case 'test':
      return <FlaskConical className={className} />;
    case 'medication':
      return <Pill className={className} />;
    case 'imaging':
      return <Scan className={className} />;
    case 'follow-up':
      return <CalendarCheck className={className} />;
    case 'referral':
      return <ArrowRight className={className} />;
    case 'procedure':
      return <Stethoscope className={className} />;
    default:
      return <Circle className={className} />;
  }
};

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

const PathwayStatusBadge: React.FC<{ status: CarePathway['status'] }> = ({ status }) => {
  const map: Record<CarePathway['status'], { variant: 'success' | 'warning' | 'info'; label: string }> = {
    active: { variant: 'success', label: 'Active' },
    completed: { variant: 'info', label: 'Completed' },
    paused: { variant: 'warning', label: 'Paused' },
  };
  const cfg = map[status] ?? { variant: 'info', label: status };
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
};

// ---------------------------------------------------------------------------
// Step node in timeline
// ---------------------------------------------------------------------------

interface StepNodeProps {
  step: CarePathwayStep;
  isLast: boolean;
}

const StepNode: React.FC<StepNodeProps> = ({ step, isLast }) => {
  const isCurrent = step.status === 'current';
  const isCompleted = step.status === 'completed';
  const isSkipped = step.status === 'skipped';

  const dateLabel = step.completedDate
    ? `Completed ${safeFormat(step.completedDate, 'MMM d, yyyy')}`
    : step.scheduledDate
      ? `Scheduled ${safeFormat(step.scheduledDate, 'MMM d, yyyy')}`
      : null;

  return (
    <li className="relative flex gap-4">
      {/* Connector line */}
      {!isLast && (
        <span
          className={cn(
            'absolute left-[19px] top-10 bottom-0 w-px',
            isCompleted ? 'bg-emerald-400/60' : 'bg-gray-200 dark:bg-gray-700',
          )}
          aria-hidden="true"
        />
      )}

      {/* Step circle / icon */}
      <div className="flex-shrink-0 flex flex-col items-center pt-1">
        <div
          className={cn(
            'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200',
            isCompleted &&
              'bg-emerald-50 border-emerald-400 text-emerald-600 dark:bg-emerald-950/40 dark:border-emerald-500 dark:text-emerald-400',
            isCurrent &&
              'bg-[#3D7A74]/10 border-[#3D7A74] text-[#3D7A74] ring-4 ring-[#3D7A74]/20 dark:bg-[#3D7A74]/20 dark:ring-[#3D7A74]/30',
            !isCompleted &&
              !isCurrent &&
              !isSkipped &&
              'bg-white border-gray-300 text-gray-400 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-500',
            isSkipped && 'bg-gray-50 border-gray-200 text-gray-300 dark:bg-gray-900 dark:border-gray-700',
          )}
          aria-hidden="true"
        >
          {isCompleted ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : isCurrent ? (
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#3D7A74] opacity-60" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#3D7A74]" />
            </span>
          ) : (
            stepTypeIcon(step.type, 'h-4 w-4')
          )}
        </div>
      </div>

      {/* Step content */}
      <div
        className={cn(
          'flex-1 pb-8 min-w-0',
          isSkipped && 'opacity-50',
        )}
      >
        <div
          className={cn(
            'rounded-xl p-4 border transition-all duration-200',
            isCurrent
              ? 'border-[#3D7A74]/40 bg-[#3D7A74]/5 dark:bg-[#3D7A74]/10 shadow-sm shadow-[#3D7A74]/10'
              : 'border-border bg-card dark:bg-card',
          )}
        >
          <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
            <h3
              className={cn(
                'text-sm font-semibold',
                isCompleted && 'text-foreground',
                isCurrent && 'text-[#3D7A74] dark:text-[#5aada5]',
                !isCompleted && !isCurrent && 'text-muted-foreground',
              )}
            >
              {step.title}
            </h3>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span
                className={cn(
                  'inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full',
                  isCompleted &&
                    'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
                  isCurrent &&
                    'bg-[#3D7A74]/10 text-[#3D7A74] dark:bg-[#3D7A74]/20 dark:text-[#5aada5]',
                  !isCompleted &&
                    !isCurrent &&
                    'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
                )}
              >
                {stepTypeIcon(step.type, 'h-3 w-3')}
                <span className="capitalize">{step.type}</span>
              </span>
            </div>
          </div>

          <p className="text-xs text-muted-foreground leading-relaxed mb-3">{step.description}</p>

          {/* Meta: date, provider, location */}
          <div className="flex flex-col gap-1">
            {dateLabel && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="h-3 w-3 flex-shrink-0" />
                <span>{dateLabel}</span>
              </div>
            )}
            {step.provider && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <User className="h-3 w-3 flex-shrink-0" />
                <span>{step.provider}</span>
              </div>
            )}
            {step.location && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span>{step.location}</span>
              </div>
            )}
            {step.notes && (
              <div className="flex items-start gap-1.5 text-xs text-muted-foreground mt-0.5">
                <StickyNote className="h-3 w-3 flex-shrink-0 mt-px" />
                <span className="italic">{step.notes}</span>
              </div>
            )}
          </div>

          {/* Links to appointment / test */}
          {(step.linkedAppointmentId || step.linkedTestId) && (
            <div className="mt-3 pt-3 border-t border-border/50">
              {step.linkedAppointmentId && (
                <Link
                  to="/appointments"
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#3D7A74] hover:text-[#2d5e5a] dark:text-[#5aada5] dark:hover:text-[#7dc4be] underline-offset-2 hover:underline transition-colors"
                >
                  View Appointment <ArrowRight className="h-3 w-3" />
                </Link>
              )}
              {step.linkedTestId && (
                <Link
                  to="/test-results"
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#3D7A74] hover:text-[#2d5e5a] dark:text-[#5aada5] dark:hover:text-[#7dc4be] underline-offset-2 hover:underline transition-colors"
                >
                  View Test Result <ArrowRight className="h-3 w-3" />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

// ---------------------------------------------------------------------------
// "What's Next" summary panel
// ---------------------------------------------------------------------------

const WhatsNextPanel: React.FC<{ pathway: CarePathway }> = ({ pathway }) => {
  const nextStep =
    pathway.steps.find((s) => s.status === 'current') ??
    pathway.steps.find((s) => s.status === 'upcoming');

  if (!nextStep) return null;

  const dateStr = nextStep.scheduledDate
    ? safeFormat(nextStep.scheduledDate, 'EEEE, MMMM d, yyyy')
    : null;

  return (
    <div className="mt-6 rounded-xl border border-[#3D7A74]/30 bg-[#3D7A74]/5 dark:bg-[#3D7A74]/10 p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#3D7A74]/15">
          <Route className="h-3.5 w-3.5 text-[#3D7A74] dark:text-[#5aada5]" />
        </div>
        <h4 className="text-sm font-semibold text-[#3D7A74] dark:text-[#5aada5]">What's Next</h4>
      </div>
      <p className="text-sm text-foreground leading-relaxed">
        <span className="font-medium">{nextStep.title}</span>
        {dateStr && (
          <>
            {' '}is scheduled for <span className="font-medium">{dateStr}</span>
          </>
        )}
        {nextStep.provider && (
          <> with <span className="font-medium">{nextStep.provider}</span></>
        )}
        {nextStep.location && (
          <> at <span className="font-medium">{nextStep.location}</span></>
        )}
        .
      </p>
      {nextStep.description && (
        <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{nextStep.description}</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Pathway Card
// ---------------------------------------------------------------------------

interface PathwayCardProps {
  pathway: CarePathway;
  isExpanded: boolean;
  onToggle: () => void;
}

const PathwayCard: React.FC<PathwayCardProps> = ({ pathway, isExpanded, onToggle }) => {
  const progress = getProgressPercent(pathway);
  const completedCount = pathway.steps.filter((s) => s.status === 'completed').length;
  const totalCount = pathway.steps.length;

  return (
    <Card
      className={cn(
        'overflow-hidden transition-shadow duration-200',
        isExpanded && 'shadow-md',
        pathway.status === 'paused' && 'opacity-75',
      )}
    >
      {/* Card header — always visible */}
      <button
        onClick={onToggle}
        className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3D7A74] focus-visible:ring-offset-1 rounded-t-lg"
        aria-expanded={isExpanded}
        aria-controls={`pathway-detail-${pathway.id}`}
      >
        <div className="p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {/* Title row */}
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <h2 className="text-base font-semibold text-foreground truncate">{pathway.name}</h2>
                <PathwayStatusBadge status={pathway.status} />
                <Badge variant="outline" className="text-[10px]">
                  {pathway.specialty}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                {pathway.description}
              </p>
            </div>
            <div className="flex-shrink-0 pt-0.5">
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-muted-foreground">
                {completedCount} of {totalCount} steps complete
              </span>
              <span className="text-xs font-semibold text-[#3D7A74] dark:text-[#5aada5]">
                {progress}%
              </span>
            </div>
            <div
              className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${pathway.name} progress: ${progress}%`}
            >
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${progress}%`,
                  background:
                    pathway.status === 'completed'
                      ? '#8CB369'
                      : 'linear-gradient(90deg, #3D7A74, #8CB369)',
                }}
              />
            </div>
          </div>

          {/* Meta row */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-muted-foreground">
            {pathway.startDate && (
              <span>Started: {safeFormat(pathway.startDate, 'MMM d, yyyy')}</span>
            )}
            {pathway.estimatedEndDate && (
              <span>Est. completion: {safeFormat(pathway.estimatedEndDate, 'MMM d, yyyy')}</span>
            )}
            {pathway.referringProvider && (
              <span>Referred by: {pathway.referringProvider}</span>
            )}
          </div>
        </div>
      </button>

      {/* Expanded timeline */}
      {isExpanded && (
        <CardContent
          id={`pathway-detail-${pathway.id}`}
          className="px-4 sm:px-5 pb-5 pt-0 border-t border-border/50"
        >
          <h3 className="text-sm font-semibold text-foreground mt-4 mb-4">Your Journey</h3>
          <ol aria-label={`${pathway.name} steps`} className="list-none m-0 p-0">
            {pathway.steps.map((step, index) => (
              <StepNode
                key={step.id}
                step={step}
                isLast={index === pathway.steps.length - 1}
              />
            ))}
          </ol>
          <WhatsNextPanel pathway={pathway} />
        </CardContent>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------

const PathwayCardSkeleton: React.FC = () => (
  <div className="rounded-xl border border-border bg-card p-5 animate-pulse" aria-hidden="true">
    <div className="flex gap-3 mb-4">
      <div className="flex-1">
        <div className="h-4 bg-muted rounded w-2/3 mb-2" />
        <div className="h-3 bg-muted rounded w-full mb-1" />
        <div className="h-3 bg-muted rounded w-4/5" />
      </div>
      <div className="h-6 w-16 bg-muted rounded-full" />
    </div>
    <div className="h-2 bg-muted rounded-full mb-3" />
    <div className="flex gap-4">
      <div className="h-3 bg-muted rounded w-24" />
      <div className="h-3 bg-muted rounded w-32" />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const CarePathwayContent: React.FC = () => {
  const { pathways, isLoading, isError, error, activeCount } = useCarePathways();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleToggle = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // Auto-expand first active pathway on initial load
  React.useEffect(() => {
    if (!isLoading && pathways.length > 0 && expandedId === null) {
      const firstActive = pathways.find((p) => p.status === 'active');
      if (firstActive) setExpandedId(firstActive.id);
    }
  }, [isLoading, pathways]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageContainer
      title="Your Care Journeys"
      description="Track your progress through each care pathway"
      icon={<Route className="h-6 w-6" />}
    >
      {/* Summary stats */}
      {!isLoading && pathways.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-2 bg-[#3D7A74]/8 dark:bg-[#3D7A74]/15 text-[#3D7A74] dark:text-[#5aada5] px-3 py-1.5 rounded-full text-xs font-medium border border-[#3D7A74]/20">
            <span className="font-bold text-sm">{activeCount}</span>
            <span>active {activeCount === 1 ? 'pathway' : 'pathways'}</span>
          </div>
          <div className="flex items-center gap-2 bg-muted/50 text-muted-foreground px-3 py-1.5 rounded-full text-xs font-medium border border-border">
            <span className="font-bold text-sm">{pathways.length}</span>
            <span>total {pathways.length === 1 ? 'journey' : 'journeys'}</span>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex flex-col gap-4" role="status" aria-label="Loading care pathways">
          <PathwayCardSkeleton />
          <PathwayCardSkeleton />
        </div>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Unable to load care pathways</p>
            <p className="text-xs mt-0.5 text-destructive/80">
              {error?.message ?? 'An unexpected error occurred. Please try again.'}
            </p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && pathways.length === 0 && (
        <div className="flex flex-col items-center justify-center text-center rounded-xl border border-dashed border-border bg-muted/30 p-10 sm:p-14">
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-muted mb-4">
            <Route className="h-7 w-7 text-muted-foreground" />
          </div>
          <h3 className="text-base font-semibold text-foreground mb-2">No active care pathways</h3>
          <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
            Your care journeys will appear here when your provider sets up a care plan for you.
          </p>
          <Button variant="outline" size="sm" className="mt-5" asChild>
            <Link to="/appointments">View Appointments</Link>
          </Button>
        </div>
      )}

      {/* Pathway list */}
      {!isLoading && !isError && pathways.length > 0 && (
        <div className="flex flex-col gap-4">
          {/* Active pathways first, then paused, then completed */}
          {[...pathways]
            .sort((a, b) => {
              const order: Record<string, number> = { active: 0, paused: 1, completed: 2 };
              return (order[a.status] ?? 9) - (order[b.status] ?? 9);
            })
            .map((pathway) => (
              <PathwayCard
                key={pathway.id}
                pathway={pathway}
                isExpanded={expandedId === pathway.id}
                onToggle={() => handleToggle(pathway.id)}
              />
            ))}
        </div>
      )}
    </PageContainer>
  );
};

/**
 * CarePathway Page — wrapped in an error boundary so a render failure
 * doesn't crash the broader app shell.
 */
const CarePathway: React.FC = () => (
  <ErrorBoundary>
    <CarePathwayContent />
  </ErrorBoundary>
);

export default CarePathway;
